import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';

import FixedFields from './common/fixedFields';

import { PrepopulatePermissions } from '../../../../../interfaces/PrepopulatePermissions';

interface DefaultReportTemplateDialogProps {
  open: boolean;
  onClose: () => void;
}

interface LargeFieldProps {
  text: string;
}

interface FieldWrapperProps {
  children?: ReactNode;
  title: string;
}

const DefaultReportTemplateDialog = ({
  open,
  onClose,
}: DefaultReportTemplateDialogProps) => {
  const fixedPermissions: PrepopulatePermissions = {
    caseManager: true,
    dateReceived: true,
    reportType: true,
    reportReference: true,
    reportSeverity: true,
  };

  const smallFieldStyles = {
    borderRadius: '6px',
    marginTop: '12px',
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
    opacity: 1,
  };

  const fieldStyles = {
    borderRadius: '6px',
    marginTop: '12px',
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#f6f6f6',
      padding: '16px',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
    opacity: 1,
  };

  const LargeField = ({ text }: LargeFieldProps) => (
    <TextField
      fullWidth
      variant='filled'
      multiline
      disabled
      sx={fieldStyles}
      minRows={10}
      placeholder={text}
      InputProps={{
        disableUnderline: true,
      }}
    />
  );

  const SmallField = ({ text }: LargeFieldProps) => (
    <TextField
      fullWidth
      hiddenLabel
      variant='filled'
      disabled
      sx={smallFieldStyles}
      placeholder={text}
      InputProps={{
        style: {
          height: '3.5rem',
          overflow: 'hidden',
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem',
          border: '',
          borderRadius: '6px',
        },
        disableUnderline: true,
      }}
    />
  );

  const FieldWrapper = ({ children, title }: FieldWrapperProps) => (
    <Box marginBottom='24px' flexGrow={1}>
      <Typography>{title}</Typography>
      {children}
    </Box>
  );

  const IntroductionSection = () => (
    <Box marginTop='40px' marginBottom='56px'>
      <Box marginBottom='16px'>
        <Typography display='inline' fontWeight={500} fontSize={20}>
          Introduction{' '}
        </Typography>
        <Typography display='inline' color='#878787'>
          (Mandatory)
        </Typography>
      </Box>
      <FieldWrapper title='Brief Description of the Report'>
        <LargeField text={'A concise summary of the reporter\'s report'} />
      </FieldWrapper>
      <FieldWrapper title='Purpose of Investigation'>
        <LargeField
          text={
            'To validate the claims made by the reporter and determine any wrongdoing'
          }
        />
      </FieldWrapper>
    </Box>
  );

  const EvidenceSection = () => (
    <Box marginBottom='56px'>
      <Box marginBottom='16px'>
        <Typography display='inline' fontWeight={500} fontSize={20}>
          Evidence Collected{' '}
        </Typography>
        <Typography display='inline' color='#878787'>
          (Mandatory)
        </Typography>
      </Box>
      <FieldWrapper title='Documents'>
        <LargeField
          text={
            'List of documents collected, such as emails, contracts, reports, etc'
          }
        />
      </FieldWrapper>
    </Box>
  );

  const FindingsSection = () => (
    <Box marginBottom='56px'>
      <Box marginBottom='16px'>
        <Typography display='inline' fontWeight={500} fontSize={20}>
          Findings{' '}
        </Typography>
        <Typography display='inline' color='#878787'>
          (Optional)
        </Typography>
      </Box>
      <FieldWrapper title='Details of Confirmed Wrongdoing'>
        <LargeField
          text={
            'Outline any confirmed misconduct, with reference to the evidence supporting each finding'
          }
        />
      </FieldWrapper>
      <FieldWrapper title='Unsubstantiated Claims'>
        <LargeField
          text={
            'Mention any claims from the original report that could not be confirmed during the investigation'
          }
        />
      </FieldWrapper>
    </Box>
  );

  const RecommendationsSection = () => (
    <Box marginBottom='56px'>
      <Box marginBottom='16px'>
        <Typography display='inline' fontWeight={500} fontSize={20}>
          Recommendations{' '}
        </Typography>
        <Typography display='inline' color='#878787'>
          (Optional)
        </Typography>
      </Box>
      <FieldWrapper title='Corrective Actions'>
        <LargeField text={'Suggested remedies to address any wrongdoing'} />
      </FieldWrapper>
    </Box>
  );

  const ConclusionSection = () => (
    <Box marginBottom='56px'>
      <Box marginBottom='16px'>
        <Typography display='inline' fontWeight={500} fontSize={20}>
          Conclusion{' '}
        </Typography>
        <Typography display='inline' color='#878787'>
          (Mandatory)
        </Typography>
      </Box>
      <FieldWrapper title='Title'>
        <SmallField text='Enter title' />
      </FieldWrapper>
      <FieldWrapper title='Conclusion'>
        <LargeField
          text={
            'Provide a concise summary of the investigation, its findings, and any next steps'
          }
        />
      </FieldWrapper>
    </Box>
  );

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1152px',
          height: '100%',
          overflow: 'auto',
        },
      }}
      disableRestoreFocus
    >
      <Box padding='40px 80px'>
        <Typography fontSize='24px'>Final Report Assessment</Typography>
        <FixedFields permissions={fixedPermissions} />
        <IntroductionSection />
        <EvidenceSection />
        <FindingsSection />
        <RecommendationsSection />
        <ConclusionSection />
      </Box>
    </Dialog>
  );
};

export default DefaultReportTemplateDialog;
